import { useState } from "react";
import { Wallet, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (s, n) => { const d = new Date(`${s}T00:00:00`); d.setDate(d.getDate() + n); return iso(d); };
const span = (a, b) => Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 86400000) + 1;

export const PortalBalanceCard = ({ b }) => {
  if (!b) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-2xl border border-emerald-200 bg-gradient-to-l from-emerald-50 to-white p-3" data-testid="portal-leave-balance">
      <p className="flex items-center gap-1.5 font-heading font-bold text-emerald-800"><Wallet className="h-4 w-4" /> رصيد إجازاتك:
        <span className="font-mono-nums text-2xl" data-testid="portal-leave-balance-value">{b.balance}</span> يوم</p>
      <span className="text-xs text-slate-500">المستحق {b.accrued} + افتتاحي {b.opening_balance} − المستخدم {b.used}{b.pending ? ` · قيد الطلب ${b.pending}` : ""}</span>
      <span className="text-xs text-slate-400">({b.annual_rate} يوماً سنوياً{b.first_mubashara ? ` منذ ${b.first_mubashara}` : ""})</span>
    </div>
  );
};

export const LeaveRangeDialog = ({ pick, balance, onClose, onSubmit }) => {
  const first = `${pick.year}-${String(pick.month).padStart(2, "0")}-01`;
  const lastOfMonth = iso(new Date(pick.year, pick.month, 0));
  const today = iso(new Date());
  const start0 = today > first && today <= lastOfMonth ? today : first;
  const [start, setStart] = useState(start0);
  const [end, setEnd] = useState(addDays(start0, 4));
  const [busy, setBusy] = useState(false);
  const days = start && end && end >= start ? span(start, end) : 0;
  const avail = balance ? Math.max(0, balance.balance - (balance.pending || 0)) : null;
  const unpaid = avail != null && days > avail ? Math.round(days - avail) : 0;
  const submit = async () => { setBusy(true); try { await onSubmit({ start_date: start, end_date: end }); } finally { setBusy(false); } };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-w-md" data-testid="leave-range-dialog">
        <DialogHeader className="text-right"><DialogTitle className="font-heading">طلب إجازة — {pick.name} {pick.year}</DialogTitle>
          <DialogDescription>حدد يوم البداية والنهاية (تبدأ داخل الشهر المختار)</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-slate-500">من<Input type="date" value={start} min={first} max={lastOfMonth} onChange={(e) => { setStart(e.target.value); if (end < e.target.value) setEnd(e.target.value); }} data-testid="leave-range-start" /></label>
          <label className="text-xs text-slate-500">إلى<Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} data-testid="leave-range-end" /></label>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-xl bg-blue-50 p-2"><p className="text-[11px] text-blue-700">أيام الطلب</p><p className="font-mono-nums text-xl font-bold text-blue-800" data-testid="leave-range-days">{days}</p></div>
          <div className="rounded-xl bg-emerald-50 p-2"><p className="text-[11px] text-emerald-700">المتاح</p><p className="font-mono-nums text-xl font-bold text-emerald-800">{avail ?? "—"}</p></div>
          <div className="rounded-xl bg-slate-50 p-2"><p className="text-[11px] text-slate-600">المتبقي بعده</p><p className="font-mono-nums text-xl font-bold text-slate-800" data-testid="leave-range-remaining">{avail != null ? Math.max(0, Math.round((avail - days) * 10) / 10) : "—"}</p></div>
        </div>
        {unpaid > 0 && <p className="flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800" data-testid="leave-range-unpaid"><AlertTriangle className="h-4 w-4" /> الطلب يتجاوز رصيدك — {unpaid} يوم ستُحتسب إجازة بدون راتب</p>}
        <Button onClick={submit} disabled={busy || !days} className="w-full" data-testid="leave-range-submit">إرسال الطلب ({days} يوم)</Button>
      </DialogContent>
    </Dialog>
  );
};
